import React, { useState } from "react";
import { Badge } from "@cloudscape-design/components";
import { ProgramData, ProgramStatus } from "../../types/configurationTypes";

// ─── Helper: Status badge ───────────────────────────────────────────────────────
function getStatusBadge(status: ProgramStatus) {
  return status === "enabled" ? (
    <Badge color="green">Enabled</Badge>
  ) : (
    <Badge color="grey">Disabled</Badge>
  );
}

// ─── Helper: Get target value from program data ─────────────────────────────────
function getTargetValue(item: ProgramData, categorySubItem: string): string {
  const ts = item.target_settings?.find((t) => t.category_subItem === categorySubItem);
  return ts ? String(ts.target_value) || "—" : "—";
}

// ─── Helper: Wrap cell content with status-based styling ────────────────────────
function wrapCell(item: ProgramData, content: React.ReactNode): JSX.Element {
  return (
    <span className={item.status === "disabled" ? "row-disabled" : ""}>
      {content}
    </span>
  );
}

// ─── Helper: Format ISO datetime to YY-MM-DD HH:mm ─────────────────────────────
function formatDateTime(isoString: string | undefined | null): string {
  if (!isoString) return "—";
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "—";
    const yy = String(date.getFullYear());
    const MM = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const HH = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${yy}-${MM}-${dd} ${HH}:${mm}`;
  } catch {
    return "—";
  }
}

// ─── Helper: Expandable Sub-Programs Cell ───────────────────────────────────────
const MAX_VISIBLE_SUB_PROGRAMS = 3;

function SubProgramsCell({ items }: { items: string[] }) {
  const [expanded, setExpanded] = useState(false);

  if (items.length <= MAX_VISIBLE_SUB_PROGRAMS) {
    return (
      <span className="sub-programs-cell">
        {items.map((name, idx) => (
          <span key={idx} className="sub-programs-cell-item">{name}</span>
        ))}
      </span>
    );
  }

  const visibleItems = expanded ? items : items.slice(0, MAX_VISIBLE_SUB_PROGRAMS);
  const hiddenCount = items.length - MAX_VISIBLE_SUB_PROGRAMS;

  return (
    <span className="sub-programs-cell">
      {visibleItems.map((name, idx) => (
        <span key={idx} className="sub-programs-cell-item">{name}</span>
      ))}
      <span
        className="sub-programs-expand-toggle"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
        style={{
          cursor: "pointer",
          color: "#0972d3",
          fontSize: "12px",
          backgroundColor: "#f2f3f3",
          padding: "2px",
          borderRadius: "4px",
          width:"60px"
        }}
      >
        {expanded ? "Show less" : `+${hiddenCount} more`}
      </span>
    </span>
  );
}

// ─── Built-in category_subItem values (used to identify custom ones) ────────────
export const BUILT_IN_CATEGORY_SUB_ITEMS = [
  "Assembly",
  "SMT",
  "Testing",
  "Machine Downtime-SMT",
  "Machine Downtime-FATP",
  "Output-SMT",
  "Output-FATP",
  "Material-SMT",
  "Material-FATP",
];

// ─── Helper: Build a column definition for a custom target category ─────────────
export function buildCustomTargetColumnDefinition(categorySubItem: string) {
  const colId = `custom_target_${categorySubItem.replace(/[^a-zA-Z0-9]/g, "_")}`;
  return {
    id: colId,
    header: categorySubItem,
    cell: (item: ProgramData) => wrapCell(item, getTargetValue(item, categorySubItem)),
    width: 130,
    minWidth: 90,
  };
}

// ─── All Column Definitions ─────────────────────────────────────────────────────
export const ALL_COLUMN_DEFINITIONS = [
  {
    id: "program_name",
    header: "Program Name",
    cell: (item: ProgramData) => wrapCell(item, <strong>{item.program_name}</strong>),
    sortingField: "program_name",
    width: 150,
    minWidth: 120,
  },
  {
    id: "program_mapping",
    header: "Sub-Programs",
    cell: (item: ProgramData) => wrapCell(
      item,
      item.program_mapping && item.program_mapping.length > 0
        ? <SubProgramsCell items={item.program_mapping} />
        : "—"
    ),
    width: 180,
    minWidth: 120,
  },
  {
    id: "cm",
    header: "CM",
    cell: (item: ProgramData) => wrapCell(item, item.cm),
    sortingField: "cm",
    width: 110,
    minWidth: 80,
  },
  {
    id: "cm_location",
    header: "Location",
    cell: (item: ProgramData) => wrapCell(item, item.cm_location),
    sortingField: "cm_location",
    width: 110,
    minWidth: 80,
  },
  {
    id: "operation",
    header: "Operation",
    cell: (item: ProgramData) => wrapCell(item, item.operation),
    sortingField: "operation",
    width: 110,
    minWidth: 80,
  },
  {
    id: "eom",
    header: "EOM",
    cell: (item: ProgramData) => wrapCell(item, item.eom || "—"),
    sortingField: "eom",
    width: 120,
    minWidth: 80,
  },
  {
    id: "ok2_ship",
    header: "OK2SHIP",
    cell: (item: ProgramData) => wrapCell(item, item.ok2_ship.split('T')[0] || "—"),
    sortingField: "ok2_ship",
    width: 150,
    minWidth: 120,
  },
  {
    id: "product_family_name",
    header: "Product Family",
    cell: (item: ProgramData) => wrapCell(item, item.product_family_name || "—"),
    width: 130,
    minWidth: 100,
  },
  {
    id: "pvt_date",
    header: "PVT Date",
    cell: (item: ProgramData) => wrapCell(item, item.pvt_date || "—"),
    sortingField: "pvt_date",
    width: 120,
    minWidth: 90,
  },
  {
    id: "ur_only",
    header: "Factory Factor only",
    cell: (item: ProgramData) => wrapCell(item, item.ur_only ? "Yes" : "No"),
    width: 90,
    minWidth: 70,
  },
  {
    id: "status",
    header: "Status",
    cell: (item: ProgramData) => wrapCell(item, getStatusBadge(item.status)),
    width: 100,
    minWidth: 80,
  },
  {
    id: "created_by",
    header: "Creator",
    cell: (item: ProgramData) => wrapCell(item, item.created_by || "—"),
    width: 100,
    minWidth: 80,
  },
  {
    id: "created_at",
    header: "Created DateTime",
    cell: (item: ProgramData) => wrapCell(item, formatDateTime(item.created_at)),
    sortingField: "created_at",
    width: 150,
    minWidth: 130,
  },
  {
    id: "updated_by",
    header: "Updater",
    cell: (item: ProgramData) => wrapCell(item, item.updated_by || "—"),
    width: 100,
    minWidth: 80,
  },
  {
    id: "updated_at",
    header: "Updated DateTime",
    cell: (item: ProgramData) => wrapCell(item, formatDateTime(item.updated_at)),
    sortingField: "updated_at",
    width: 150,
    minWidth: 130,
  },
  // Target columns (hidden by default) — order matches DEFAULT_TARGET_SETTINGS
  {
    id: "assemblyFatpTarget",
    header: "Assembly",
    cell: (item: ProgramData) => wrapCell(item, getTargetValue(item, "Assembly")),
    width: 110,
    minWidth: 80,
  },
  {
    id: "testingSmtTarget",
    header: "SMT",
    cell: (item: ProgramData) => wrapCell(item, getTargetValue(item, "SMT")),
    width: 110,
    minWidth: 80,
  },
  {
    id: "testingFatpTarget",
    header: "Testing",
    cell: (item: ProgramData) => wrapCell(item, getTargetValue(item, "Testing")),
    width: 110,
    minWidth: 80,
  },
  {
    id: "machineDowntimeSmtTarget",
    header: "Machine Downtime-SMT",
    cell: (item: ProgramData) => wrapCell(item, getTargetValue(item, "Machine Downtime-SMT")),
    width: 170,
    minWidth: 140,
  },
  {
    id: "machineDowntimeFatpTarget",
    header: "Machine Downtime-FATP",
    cell: (item: ProgramData) => wrapCell(item, getTargetValue(item, "Machine Downtime-FATP")),
    width: 170,
    minWidth: 140,
  },
  {
    id: "outputSmtTarget",
    header: "Output-SMT",
    cell: (item: ProgramData) => wrapCell(item, getTargetValue(item, "Output-SMT")),
    width: 110,
    minWidth: 80,
  },
  {
    id: "outputFatpTarget",
    header: "Output-FATP",
    cell: (item: ProgramData) => wrapCell(item, getTargetValue(item, "Output-FATP")),
    width: 110,
    minWidth: 80,
  },
  {
    id: "materialSmtTarget",
    header: "Material-SMT",
    cell: (item: ProgramData) => wrapCell(item, getTargetValue(item, "Material-SMT")),
    width: 110,
    minWidth: 80,
  },
  {
    id: "materialFatpTarget",
    header: "Material-FATP",
    cell: (item: ProgramData) => wrapCell(item, getTargetValue(item, "Material-FATP")),
    width: 120,
    minWidth: 80,
  },
];
